import { useEffect, useRef, useState } from 'react';
import { Tooltip } from 'antd';
import { ArrowDownIcon } from 'icons';

interface IOptions {
  label: string;
  value: string | number;
}

interface IProps {
  options?: IOptions[];
  value?: string | number | (string | number)[];
  onChange: (value: string | number | (string | number)[]) => void;
  placeholder?: string;
  className?: string;
  dropDownClassName?: string;
  disabled?: boolean;
  multiple?: boolean;
  maxVisibleTags?: number;
}

export const Select: React.FC<IProps> = ({
  options = [],
  value,
  onChange,
  placeholder = 'Select...',
  className = '',
  dropDownClassName = '',
  disabled = false,
  multiple = false,
  maxVisibleTags = 1
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  const handleOptionSelect = (option: string | number) => {
    if (multiple) {
      const currentValue = Array.isArray(value) ? value : [];
      const newValue = currentValue.includes(option)
        ? currentValue.filter(v => v !== option)
        : [...currentValue, option];

      onChange(newValue);
    } else {
      onChange(option);
      setIsOpen(false);
    }
  };

  const displayValue = (() => {
    if (multiple && Array.isArray(value)) {
      const selectedLabels = options.filter(opt => value.includes(opt.value)).map(opt => opt.label);

      return selectedLabels.length > 0 ? selectedLabels.join(', ') : placeholder;
    }
    const selectedOption = options.find(opt => opt.value === value);

    return selectedOption ? selectedOption.label : placeholder;
  })();

  const selectedOptions = multiple && Array.isArray(value) ? options.filter(opt => value.includes(opt.value)) : [];

  const visibleSelectedLabels = selectedOptions
    .slice(0, maxVisibleTags)
    .map(opt => opt.label)
    .join(', ');
  const hiddenSelectedLabels = selectedOptions
    .slice(maxVisibleTags)
    .map(opt => opt.label)
    .join(', ');
  const hiddenCount = selectedOptions.length - maxVisibleTags;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative" ref={selectRef}>
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`flex min-w-20 items-center justify-between gap-2 rounded-full bg-white px-4 py-1.5 transition-colors duration-200 ${className} ${
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:border-gray-400'
        }`}
      >
        <span className={`flex-1 truncate text-left text-sm ${value ? 'text-gray-700' : 'text-gray-500'}`}>
          {multiple && Array.isArray(value) ? visibleSelectedLabels || placeholder : displayValue}
        </span>

        {multiple && hiddenCount > 0 && (
          <Tooltip title={hiddenSelectedLabels}>
            <span className="inline-block rounded-full bg-gray-200 px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-300">
              +{hiddenCount}
            </span>
          </Tooltip>
        )}

        <ArrowDownIcon
          className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Options */}
      <div
        className={`z-11 absolute left-0 top-full mt-1 min-w-32 rounded-lg border border-gray-200 bg-white shadow-lg transition-all duration-200 ${dropDownClassName} ${
          isOpen && !disabled
            ? 'translate-y-0 transform opacity-100'
            : 'pointer-events-none -translate-y-2 transform opacity-0'
        }`}
      >
        {options.map((option, index) => {
          const isSelected = Array.isArray(value) ? value.includes(option.value) : value === option.value;

          return (
            <button
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              onClick={() => handleOptionSelect(option.value)}
              className={`w-full cursor-pointer px-4 py-2 text-left text-sm transition-colors duration-150 first:rounded-t-lg last:rounded-b-lg hover:bg-green-50 hover:text-green-600 ${
                isSelected ? 'bg-green-50 text-green-600' : 'text-gray-700'
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};
